"use client";

import clsx from "clsx";

export interface Order {
  id: string;
  asset: string;
  type: "market" | "limit";
  side: "long" | "short";
  size: number;
  price: number | null;
  filledSize: number;
  avgFillPrice: number | null;
  status: "pending" | "open" | "filled" | "partially_filled" | "cancelled" | "expired";
  leverage: number;
  fee: number;
  createdAt: Date;
  filledAt: Date | null;
}

interface OrdersTableProps {
  orders: Order[];
  onCancelOrder?: (orderId: string) => void;
  isLoading?: boolean;
  showOpenOnly?: boolean;
}

const statusConfig: Record<Order["status"], { label: string; color: string }> = {
  pending: { label: "Pending", color: "text-amber-400 bg-amber-400/20" },
  open: { label: "Open", color: "text-blue-400 bg-blue-400/20" },
  filled: { label: "Filled", color: "text-emerald-400 bg-emerald-400/20" },
  partially_filled: { label: "Partial", color: "text-cyan-400 bg-cyan-400/20" },
  cancelled: { label: "Cancelled", color: "text-slate-400 bg-slate-400/20" },
  expired: { label: "Expired", color: "text-slate-500 bg-slate-500/20" },
};

export const OrdersTable = ({
  orders,
  onCancelOrder,
  isLoading = false,
  showOpenOnly = false,
}: OrdersTableProps) => {
  const filteredOrders = showOpenOnly
    ? orders.filter(
        (o) =>
          o.status === "open" || o.status === "pending" || o.status === "partially_filled"
      )
    : orders;

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 rounded bg-slate-700/50" />
        ))}
      </div>
    );
  }

  if (filteredOrders.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200/60 bg-white/5 p-8 text-center">
        <p className="text-sm text-slate-500">
          {showOpenOnly ? "No open orders" : "No order history"}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200/60 bg-white/5">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-700 text-left text-slate-500">
            <th className="px-4 py-3 font-medium">Time</th>
            <th className="px-4 py-3 font-medium">Asset</th>
            <th className="px-4 py-3 font-medium">Type</th>
            <th className="px-4 py-3 font-medium">Side</th>
            <th className="px-4 py-3 font-medium text-right">Size</th>
            <th className="px-4 py-3 font-medium text-right">Price</th>
            <th className="px-4 py-3 font-medium text-right">Filled</th>
            <th className="px-4 py-3 font-medium">Status</th>
            {showOpenOnly && <th className="px-4 py-3 font-medium">Action</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700/50">
          {filteredOrders.map((order) => {
            const status = statusConfig[order.status];
            const canCancel = order.status === "open" || order.status === "pending";

            return (
              <tr key={order.id} className="hover:bg-slate-800/30">
                <td className="whitespace-nowrap px-4 py-3 text-slate-400">
                  {new Date(order.createdAt).toLocaleString()}
                </td>
                <td className="px-4 py-3 font-medium text-white">{order.asset}</td>
                <td className="px-4 py-3">
                  <span className="rounded bg-slate-700 px-2 py-0.5 text-slate-300">
                    {order.type.toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={clsx(
                      "rounded px-2 py-0.5 font-semibold",
                      order.side === "long"
                        ? "bg-emerald-500/20 text-emerald-400"
                        : "bg-rose-500/20 text-rose-400"
                    )}
                  >
                    {order.side.toUpperCase()} {order.leverage}x
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-white">
                  ${order.size.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right text-white">
                  {order.price ? `$${order.price.toLocaleString()}` : "Market"}
                </td>
                <td className="px-4 py-3 text-right text-slate-300">
                  {order.avgFillPrice ? `$${order.avgFillPrice.toLocaleString()}` : "-"}
                  <span className="ml-1 text-slate-500">
                    ({((order.filledSize / order.size) * 100).toFixed(0)}%)
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={clsx("rounded-full px-2 py-0.5 text-xs", status.color)}
                  >
                    {status.label}
                  </span>
                </td>
                {showOpenOnly && (
                  <td className="px-4 py-3">
                    {canCancel && (
                      <button
                        onClick={() => onCancelOrder?.(order.id)}
                        className="rounded bg-slate-700 px-2 py-1 text-xs text-white transition hover:bg-slate-600"
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
